import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconFileText, IconMapPin } from '@tabler/icons-react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';

const POPOVER_Z = 14000;

function withPopoverZ(style) {
  if (!style) return null;
  return { ...style, zIndex: POPOVER_Z };
}

function norm(v) {
  return String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function equipCountOfLocal(l) {
  return Number(l?.qtdEquipamentos) || 0;
}

function equipCountOfContrato(c) {
  if (c?.qtdEquipamentos != null) return Number(c.qtdEquipamentos) || 0;
  return (c?.locais || []).reduce((s, l) => s + equipCountOfLocal(l), 0);
}

function sortLocaisByEquip(locais) {
  return [...(locais || [])].sort((a, b) => {
    const diff = equipCountOfLocal(b) - equipCountOfLocal(a);
    if (diff !== 0) return diff;
    return String(a.nomeSite || '').localeCompare(String(b.nomeSite || ''), 'pt-BR');
  });
}

function Contr2TruncateTip({ text, className }) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const label = text || '—';

  const hide = useCallback(() => setTip(null), []);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el || !text) return;
    if (el.scrollWidth <= el.clientWidth + 1) return;
    const r = el.getBoundingClientRect();
    setTip({ text, top: r.top, left: r.left + r.width / 2 });
  }, [text]);

  return (
    <>
      <span
        ref={ref}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {label}
      </span>
      {tip && typeof document !== 'undefined'
        ? createPortal(
            <span
              className="uc-upf-contr2-tip"
              role="tooltip"
              style={{ top: tip.top, left: tip.left }}
            >
              <span className="uc-upf-contr2-tip-arrow" aria-hidden />
              {tip.text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}

/**
 * Layout experimental Contratos 2 — split disponível / com acesso (padrão mscad-equip-panel).
 */
export default function UserPortalContratos2Panel({
  contratos = [],
  contratosLoading = false,
  selectedLocalIds,
  onToggleLocal,
  onClearAll,
  onSelecionarTodosDoContrato,
  permEnderecoErro = false,
  expandedContratoIds,
  onToggleContrato,
}) {
  const [busca, setBusca] = useState('');
  const [filtroContratos, setFiltroContratos] = useState([]);
  const [filtroSites, setFiltroSites] = useState([]);
  const [contrOpen, setContrOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [contrSearch, setContrSearch] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [linkedExpanded, setLinkedExpanded] = useState(() => new Set());

  const contrBtnRef = useRef(null);
  const siteBtnRef = useRef(null);
  const contrPopoverStyle = useEcFilterPopoverLayout(contrBtnRef, contrOpen, 260, 320);
  const sitePopoverStyle = useEcFilterPopoverLayout(siteBtnRef, siteOpen, 260, 320);

  useEffect(() => {
    if (!contrOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-uc-contr2-popover="contrato"]');
      if (pop?.contains(e.target) || contrBtnRef.current?.contains(e.target)) return;
      setContrOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setContrOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [contrOpen]);

  useEffect(() => {
    if (!siteOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-uc-contr2-popover="site"]');
      if (pop?.contains(e.target) || siteBtnRef.current?.contains(e.target)) return;
      setSiteOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setSiteOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [siteOpen]);

  const contratoOptions = useMemo(
    () =>
      [...contratos]
        .map((c) => ({ value: c.id, label: c.contrato || c.id }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR')),
    [contratos]
  );

  const siteOptions = useMemo(() => {
    const map = new Map();
    contratos.forEach((c) => {
      (c.locais || []).forEach((l) => {
        const name = String(l.nomeSite || '').trim();
        if (!name) return;
        if (!map.has(name)) map.set(name, { value: name, label: name });
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [contratos]);

  const filteredContrOptions = useMemo(() => {
    const q = norm(contrSearch);
    if (!q) return contratoOptions;
    return contratoOptions.filter((i) => norm(i.label).includes(q));
  }, [contratoOptions, contrSearch]);

  const filteredSiteOptions = useMemo(() => {
    const q = norm(siteSearch);
    if (!q) return siteOptions;
    return siteOptions.filter((i) => norm(i.label).includes(q));
  }, [siteOptions, siteSearch]);

  const hasFilters =
    filtroContratos.length > 0 || filtroSites.length > 0 || String(busca).trim().length > 0;

  const clearFilters = useCallback(() => {
    setFiltroContratos([]);
    setFiltroSites([]);
    setBusca('');
  }, []);

  const filteredContratos = useMemo(() => {
    const q = norm(busca);
    const contrSet = new Set(filtroContratos);
    const siteSet = new Set(filtroSites.map(norm));

    return contratos
      .filter((c) => {
        if (contrSet.size > 0 && !contrSet.has(c.id)) return false;

        const locais = c.locais || [];
        if (siteSet.size > 0) {
          const hasSite = locais.some((l) => siteSet.has(norm(l.nomeSite)));
          if (!hasSite) return false;
        }

        if (!q) return true;
        if (norm(c.contrato).includes(q) || norm(c.classificacao).includes(q)) return true;
        return locais.some(
          (l) => norm(l.nomeSite).includes(q) || norm(l.endereco).includes(q)
        );
      })
      .map((c) => {
        let locais = c.locais || [];
        if (siteSet.size > 0) {
          locais = locais.filter((l) => siteSet.has(norm(l.nomeSite)));
        }
        if (q && !norm(c.contrato).includes(q) && !norm(c.classificacao).includes(q)) {
          locais = locais.filter(
            (l) => norm(l.nomeSite).includes(q) || norm(l.endereco).includes(q)
          );
        }
        return {
          ...c,
          locais: sortLocaisByEquip(locais),
          qtdEquipamentos: locais.reduce((s, l) => s + equipCountOfLocal(l), 0),
        };
      })
      .filter((c) => (c.locais || []).length > 0 || (!siteSet.size && !q))
      .sort((a, b) => {
        const diff = equipCountOfContrato(b) - equipCountOfContrato(a);
        if (diff !== 0) return diff;
        return String(a.contrato || '').localeCompare(String(b.contrato || ''), 'pt-BR');
      });
  }, [contratos, filtroContratos, filtroSites, busca]);

  const locaisSelecionadosAgrupados = useMemo(() => {
    const meta = new Map();
    contratos.forEach((c) => {
      (c.locais || []).forEach((l) => {
        meta.set(l.id, {
          local: l,
          contratoId: c.id,
          contrato: c.contrato,
        });
      });
    });
    const grupos = new Map();
    selectedLocalIds.forEach((lid) => {
      const m = meta.get(lid);
      if (!m) return;
      if (!grupos.has(m.contratoId)) {
        grupos.set(m.contratoId, {
          contratoId: m.contratoId,
          contrato: m.contrato,
          locais: [],
          qtdEquipamentos: 0,
        });
      }
      grupos.get(m.contratoId).locais.push(m.local);
    });
    return [...grupos.values()]
      .map((g) => ({
        ...g,
        locais: sortLocaisByEquip(g.locais),
        qtdEquipamentos: g.locais.reduce((s, l) => s + equipCountOfLocal(l), 0),
      }))
      .sort((a, b) => {
        const diff = (Number(b.qtdEquipamentos) || 0) - (Number(a.qtdEquipamentos) || 0);
        if (diff !== 0) return diff;
        return String(a.contrato || '').localeCompare(String(b.contrato || ''), 'pt-BR');
      });
  }, [contratos, selectedLocalIds]);

  const toggleFiltroContrato = (id) => {
    setFiltroContratos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleFiltroSite = (name) => {
    setFiltroSites((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );
  };

  const toggleLinkedGroup = (contratoId) => {
    setLinkedExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(contratoId)) n.delete(contratoId);
      else n.add(contratoId);
      return n;
    });
  };

  return (
    <div className="uc-upf-contr2">
      {contratosLoading ? (
        <p className="uc-upf-contr-loading">Carregando contratos e locais…</p>
      ) : null}

      {permEnderecoErro ? (
        <p className="uc-upf-contr-err-banner" role="alert">
          Com permissões de Operações marcadas, selecione ao menos um endereço (local).
        </p>
      ) : null}

      <div className="uc-upf-contr2-filters">
        <EcClickUpFilterChip
          anchorRef={contrBtnRef}
          label="Contrato"
          value={filtroContratos.length > 0 ? String(filtroContratos.length) : null}
          icon={IconFileText}
          hasSelection={filtroContratos.length > 0}
          isOpen={contrOpen}
          ariaLabel="Filtrar por contrato"
          onClick={() => {
            setSiteOpen(false);
            setContrOpen((o) => !o);
          }}
        />
        {contrOpen && contrPopoverStyle
          ? createPortal(
              <div
                data-uc-contr2-popover="contrato"
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade`}
                style={withPopoverZ(buildEcFilterPopoverStyle(contrPopoverStyle))}
                role="dialog"
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar contrato…"
                  searchTerm={contrSearch}
                  onSearchChange={setContrSearch}
                  items={filteredContrOptions}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={filtroContratos}
                  onToggle={(item) => toggleFiltroContrato(item.value)}
                  emptyMessage="Nenhum contrato"
                  listAriaLabel="Contratos"
                  bulkMenuEnabled
                  onBulkToggleAll={() => {
                    const keys = filteredContrOptions.map((i) => i.value);
                    const allOn = keys.length > 0 && keys.every((k) => filtroContratos.includes(k));
                    setFiltroContratos(allOn ? [] : keys);
                  }}
                />
              </div>,
              document.body
            )
          : null}

        <EcClickUpFilterChip
          anchorRef={siteBtnRef}
          label="Site"
          value={filtroSites.length > 0 ? String(filtroSites.length) : null}
          icon={IconMapPin}
          hasSelection={filtroSites.length > 0}
          isOpen={siteOpen}
          ariaLabel="Filtrar por site"
          onClick={() => {
            setContrOpen(false);
            setSiteOpen((o) => !o);
          }}
        />
        {siteOpen && sitePopoverStyle
          ? createPortal(
              <div
                data-uc-contr2-popover="site"
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade`}
                style={withPopoverZ(buildEcFilterPopoverStyle(sitePopoverStyle))}
                role="dialog"
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar site…"
                  searchTerm={siteSearch}
                  onSearchChange={setSiteSearch}
                  items={filteredSiteOptions}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={filtroSites}
                  onToggle={(item) => toggleFiltroSite(item.value)}
                  emptyMessage="Nenhum site"
                  listAriaLabel="Sites"
                  bulkMenuEnabled
                  onBulkToggleAll={() => {
                    const keys = filteredSiteOptions.map((i) => i.value);
                    const allOn = keys.length > 0 && keys.every((k) => filtroSites.includes(k));
                    setFiltroSites(allOn ? [] : keys);
                  }}
                />
              </div>,
              document.body
            )
          : null}

        <BoardToolbarExpandSearch
          value={busca}
          onChange={setBusca}
          placeholder="Buscar contrato, site ou endereço…"
          ariaLabel="Buscar contratos e locais"
        />

        {hasFilters ? (
          <button type="button" className="uc-upf-contr2-clear-filters" onClick={clearFilters}>
            Limpar filtros
          </button>
        ) : null}

        <span className="uc-upf-contr2-filter-count">
          {filteredContratos.length} contrato(s)
        </span>
      </div>

      <div className="uc-upf-contr2-main">
        <div className="uc-upf-contr2-available">
          <div className="uc-upf-contr2-available-head">
            <span className="uc-upf-contr2-linked-title">
              Contratos e sites · {filteredContratos.length}
            </span>
          </div>
          <div className="uc-upf-contr2-scroll">
            {filteredContratos.length === 0 ? (
              <div className="uc-upf-contr2-empty">
                <div className="uc-upf-contr2-empty-title">Nenhum disponível</div>
                <div className="uc-upf-contr2-empty-hint">
                  Não há contratos ou sites com os filtros atuais.
                </div>
              </div>
            ) : (
              filteredContratos.map((c) => {
                const exp = expandedContratoIds.has(c.id);
                const locais = c.locais || [];
                const allOn =
                  locais.length > 0 && locais.every((l) => selectedLocalIds.has(l.id));
                return (
                  <div key={c.id} className="uc-upf-contr2-group">
                    <button
                      type="button"
                      className="uc-upf-contr2-group-head"
                      onClick={() => onToggleContrato(c.id)}
                      aria-expanded={exp}
                    >
                      <span className="uc-upf-contr2-chevron" aria-hidden>
                        {exp ? (
                          <ChevronDown size={13} strokeWidth={2} />
                        ) : (
                          <ChevronRight size={13} strokeWidth={2} />
                        )}
                      </span>
                      <span className="uc-upf-contr2-group-code" title={c.contrato}>
                        {c.contrato}
                      </span>
                      <span className="uc-upf-contr2-group-class" title={c.classificacao}>
                        {c.classificacao}
                      </span>
                      <span
                        className="uc-upf-contr2-group-count"
                        title={`${equipCountOfContrato(c)} equipamento(s)`}
                      >
                        {equipCountOfContrato(c)}
                      </span>
                      <span
                        className="uc-upf-contr2-group-bulk"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelecionarTodosDoContrato?.(c, !allOn);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelecionarTodosDoContrato?.(c, !allOn);
                          }
                        }}
                      >
                        {allOn ? 'rem. todos' : 'sel. todos'}
                      </span>
                    </button>
                    {exp
                      ? locais.map((l) => {
                          const on = selectedLocalIds.has(l.id);
                          return (
                            <div
                              key={l.id}
                              className={[
                                'uc-upf-contr2-local-row',
                                on ? 'uc-upf-contr2-local-row--on' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => onToggleLocal(l.id)}
                            >
                              <label
                                className="condicoes-install-toggle uc-upf-loc-toggle"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => onToggleLocal(l.id)}
                                  aria-label={
                                    on
                                      ? `Remover acesso: ${l.nomeSite || l.endereco}`
                                      : `Habilitar acesso: ${l.nomeSite || l.endereco}`
                                  }
                                />
                                <span className="condicoes-install-pill">
                                  <span className="condicoes-install-dot" />
                                </span>
                              </label>
                              <Contr2TruncateTip
                                className="uc-upf-contr2-local-name"
                                text={l.nomeSite || l.endereco || ''}
                              />
                              <span
                                className="uc-upf-contr2-site-equip"
                                title={`${equipCountOfLocal(l)} equipamento(s)`}
                              >
                                {equipCountOfLocal(l)}
                              </span>
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="uc-upf-contr2-linked">
          <div className="uc-upf-contr2-linked-head">
            <span className="uc-upf-contr2-linked-title">
              Locais com acesso · {selectedLocalIds.size}
            </span>
            {selectedLocalIds.size > 0 ? (
              <button type="button" className="uc-upf-contr2-linked-clear" onClick={onClearAll}>
                Limpar tudo
              </button>
            ) : null}
          </div>
          <div className="uc-upf-contr2-scroll uc-upf-contr2-scroll--linked">
            {locaisSelecionadosAgrupados.length === 0 ? (
              <div className="uc-upf-contr2-empty">
                <div className="uc-upf-contr2-empty-title">Nenhum local com acesso</div>
                <div className="uc-upf-contr2-empty-hint">
                  Selecione locais à esquerda para conceder acesso.
                </div>
              </div>
            ) : (
              locaisSelecionadosAgrupados.map((g) => {
                const expanded = linkedExpanded.has(g.contratoId);
                return (
                  <div key={g.contratoId} className="uc-upf-contr2-group">
                    <button
                      type="button"
                      className="uc-upf-contr2-group-head uc-upf-contr2-group-head--linked"
                      onClick={() => toggleLinkedGroup(g.contratoId)}
                      aria-expanded={expanded}
                    >
                      <span className="uc-upf-contr2-chevron" aria-hidden>
                        {expanded ? (
                          <ChevronDown size={13} strokeWidth={2} />
                        ) : (
                          <ChevronRight size={13} strokeWidth={2} />
                        )}
                      </span>
                      <span className="uc-upf-contr2-group-code">{g.contrato}</span>
                      <span
                        className="uc-upf-contr2-group-count"
                        title={`${g.qtdEquipamentos || 0} equipamento(s)`}
                      >
                        {g.qtdEquipamentos || 0}
                      </span>
                      <span
                        className="uc-upf-contr2-group-bulk uc-upf-contr2-group-bulk--linked"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelecionarTodosDoContrato?.(
                            { id: g.contratoId, locais: g.locais },
                            false
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelecionarTodosDoContrato?.(
                              { id: g.contratoId, locais: g.locais },
                              false
                            );
                          }
                        }}
                      >
                        rem. todos
                      </span>
                    </button>
                    {expanded
                      ? g.locais.map((l) => (
                          <div
                            key={l.id}
                            className="uc-upf-contr2-linked-row"
                            onClick={() => onToggleLocal(l.id)}
                          >
                            <Contr2TruncateTip
                              className="uc-upf-contr2-linked-name"
                              text={l.nomeSite || l.endereco || ''}
                            />
                            <span
                              className="uc-upf-contr2-site-equip"
                              title={`${equipCountOfLocal(l)} equipamento(s)`}
                            >
                              {equipCountOfLocal(l)}
                            </span>
                            <X
                              size={13}
                              strokeWidth={2}
                              className="uc-upf-contr2-linked-remove"
                              aria-hidden
                            />
                          </div>
                        ))
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
